import { z } from "zod";

import {
  greenarrowEmailConfigSchema,
  infobipTextConfigSchema,
  type Site,
  type SiteListing,
  type SitePatch,
} from "@/api/zero/sites";

export type SiteFieldKind =
  | "text"
  | "number"
  | "boolean"
  | "stringList"
  | "json";

export type FormValue = string | number | boolean | string[];

export interface SiteFieldDescriptor {
  key: keyof Site;
  label: string;
  kind: SiteFieldKind;
  schema?: z.ZodType;
  options?: readonly string[];
  readOnly?: boolean;
  helperText?: string;
}

const emailOrEmpty = z.union([
  z.literal(""),
  z.string().email("Must be a valid email address"),
]);

const currencyCode = z.union([
  z.literal(""),
  z.string().regex(/^[A-Z]{3}$/, "Must be a 3-letter ISO code, e.g. USD"),
]);

const nonNegativeCount = z
  .number()
  .int("Must be a whole number")
  .min(0, "Must be 0 or greater");

// The four free-form jsonb columns are proto Structs: any JSON object (or null
// to clear). The two typed configs validate against their real shapes (strict,
// so a mistyped key surfaces here instead of being silently dropped server-side
// by the gateway's DiscardUnknown).
const jsonStructSchema = z.record(z.string(), z.unknown()).nullable();

const emailProviderConfigEditSchema = z
  .object({ greenarrow: greenarrowEmailConfigSchema.strict().nullish() })
  .strict()
  .nullable();

const textProviderConfigEditSchema = z
  .object({ infobip: infobipTextConfigSchema.strict().nullish() })
  .strict()
  .nullable();

export interface SiteFieldset {
  title: string;
  description?: string;
  fields: SiteFieldDescriptor[];
}

export const SITE_FIELDSETS: SiteFieldset[] = [
  {
    title: "Basic info",
    description: "Identity, locale and platform for this site.",
    fields: [
      { key: "url", label: "URL", kind: "text" },
      { key: "brand_name", label: "Brand name", kind: "text" },
      { key: "timezone", label: "Timezone", kind: "text" },
      {
        key: "currency",
        label: "Currency",
        kind: "text",
        schema: currencyCode,
      },
      {
        key: "platform",
        label: "Platform",
        kind: "text",
        options: [
          "",
          "magento",
          "magento2",
          "shopify",
          "shopify_plus",
          "woocommerce",
          "commercev3",
          "cartdotcom",
          "custom",
        ],
      },
      { key: "myshopify_url", label: "myshopify URL", kind: "text" },
      { key: "tags", label: "Tags", kind: "stringList" },
    ],
  },
  {
    title: "Email & delivery",
    description: "Sender identity, provider, frequency caps and suppression.",
    fields: [
      { key: "default_from_name", label: "From name", kind: "text" },
      {
        key: "default_from_email",
        label: "From email",
        kind: "text",
        schema: emailOrEmpty,
      },
      {
        key: "default_reply_to",
        label: "Reply-to",
        kind: "text",
        schema: emailOrEmpty,
      },
      {
        key: "sender_domain",
        label: "Sender domain",
        kind: "text",
        readOnly: true,
        helperText: "Set up and removed in Settings → Email → Authentication.",
      },
      {
        key: "email_provider",
        label: "Email provider",
        kind: "text",
        // connect-api allows "" (unset) or "greenarrow" (site_mapper.go).
        options: ["", "greenarrow"],
        schema: z.union([z.literal(""), z.literal("greenarrow")], {
          message: "connect-api only supports greenarrow (or unset)",
        }),
      },
      { key: "preheader_styles", label: "Preheader styles", kind: "text" },
      {
        key: "email_validation_enabled",
        label: "Email validation enabled",
        kind: "boolean",
      },
      {
        key: "append_footer_to_emails",
        label: "Append footer to emails",
        kind: "boolean",
      },
      {
        key: "frequency_cap_day_count",
        label: "Frequency cap (day)",
        kind: "number",
        schema: nonNegativeCount,
      },
      {
        key: "frequency_cap_week_count",
        label: "Frequency cap (week)",
        kind: "number",
        schema: nonNegativeCount,
      },
      {
        key: "frequency_cap_month_count",
        label: "Frequency cap (month)",
        kind: "number",
        schema: nonNegativeCount,
      },
      {
        key: "bcc_emails_enabled",
        label: "BCC emails enabled",
        kind: "boolean",
      },
      {
        key: "bcc_email_address",
        label: "BCC email address",
        kind: "text",
        schema: emailOrEmpty,
      },
      {
        key: "suppressed_domains",
        label: "Suppressed domains",
        kind: "stringList",
      },
      {
        key: "disallowed_zb_status_codes",
        label: "Disallowed ZB status codes",
        kind: "stringList",
      },
    ],
  },
  {
    title: "Personalization & recommendations",
    description: "What the recommendation engine may draw from.",
    fields: [
      {
        key: "recommendations_days_back",
        label: "Recommendation days back",
        kind: "number",
        schema: nonNegativeCount,
      },
      {
        key: "recommendations_ignored_products",
        label: "Ignored products",
        kind: "stringList",
      },
      {
        key: "recommendations_ignored_skus",
        label: "Ignored SKUs",
        kind: "stringList",
      },
      {
        key: "recommendations_ignored_categories",
        label: "Ignored categories",
        kind: "stringList",
      },
      {
        key: "recommendations_ignored_brands",
        label: "Ignored brands",
        kind: "stringList",
      },
    ],
  },
  {
    title: "Analytics",
    description: "Third-party analytics tracking.",
    fields: [
      {
        key: "google_analytics_id",
        label: "Google Analytics ID",
        kind: "text",
      },
      {
        key: "google_analytics_is_active",
        label: "Google Analytics active",
        kind: "boolean",
      },
    ],
  },
  {
    title: "Regional Compliance",
    description: "Regional consent rules applied before sending.",
    fields: [
      {
        key: "restrict_email_eligibility_to_opt_in",
        label: "Restrict to explicit opt-in",
        kind: "boolean",
      },
    ],
  },
  {
    title: "GDPR",
    description: "Data-subject request handling for this site.",
    fields: [
      { key: "gdpr_enabled", label: "GDPR enabled", kind: "boolean" },
      {
        key: "gdpr_request_email",
        label: "GDPR request email",
        kind: "text",
        schema: emailOrEmpty,
      },
      {
        key: "gdpr_results_email",
        label: "GDPR results email",
        kind: "text",
        schema: emailOrEmpty,
      },
    ],
  },
  {
    title: "Toggles",
    description: "Feature switches for this site.",
    fields: [
      { key: "is_connect", label: "Is Connect", kind: "boolean" },
      { key: "is_internal", label: "Is Internal", kind: "boolean" },
      {
        key: "monitoring_enabled",
        label: "Monitoring enabled",
        kind: "boolean",
      },
      {
        key: "tracking_links_enabled",
        label: "Tracking links enabled",
        kind: "boolean",
      },
      { key: "show_branding", label: "Show branding", kind: "boolean" },
      {
        key: "two_step_unsubscribe",
        label: "Two-step unsubscribe",
        kind: "boolean",
      },
      { key: "frames_enabled", label: "Frames enabled", kind: "boolean" },
      {
        key: "is_skustore_custom_field_mapping_enabled",
        label: "SKU Store custom field mapping enabled",
        kind: "boolean",
      },
      { key: "test_mode", label: "Test mode", kind: "boolean" },
    ],
  },
  {
    title: "SMS",
    description: "Text provider and sending number.",
    fields: [
      { key: "sms_enabled", label: "SMS enabled", kind: "boolean" },
      {
        key: "text_provider",
        label: "Text provider",
        kind: "text",
        options: ["", "infobip"],
        schema: z.union([z.literal(""), z.literal("infobip")], {
          message: "connect-api only supports infobip",
        }),
      },
      {
        key: "sms_primary_phone_number",
        label: "Primary phone number",
        kind: "text",
      },
    ],
  },
  {
    title: "Advanced configuration (JSON)",
    description:
      "Raw provider and feature config. Leave a field empty to clear it.",
    fields: [
      {
        key: "email_provider_config",
        label: "Email provider config",
        kind: "json",
        schema: emailProviderConfigEditSchema,
      },
      {
        key: "text_provider_config",
        label: "Text provider config",
        kind: "json",
        schema: textProviderConfigEditSchema,
      },
      {
        key: "model_distribution_config",
        label: "Model distribution config",
        kind: "json",
        schema: jsonStructSchema,
      },
      {
        key: "frames_config",
        label: "Frames config",
        kind: "json",
        schema: jsonStructSchema,
      },
      {
        key: "onboarding_metadata",
        label: "Onboarding metadata",
        kind: "json",
        schema: jsonStructSchema,
      },
      {
        key: "csv_integration_data",
        label: "CSV integration data",
        kind: "json",
        schema: jsonStructSchema,
      },
    ],
  },
];

export const SITE_FIELD_DESCRIPTORS: SiteFieldDescriptor[] =
  SITE_FIELDSETS.flatMap((fieldset) => fieldset.fields);

export type SiteFormValues = Record<string, FormValue>;

function emptyValueFor(kind: SiteFieldKind): FormValue {
  switch (kind) {
    case "text":
      return "";
    case "number":
      return 0;
    case "boolean":
      return false;
    case "stringList":
      return [];
    case "json":
      return "";
  }
}

function normalize(kind: SiteFieldKind, raw: unknown): FormValue {
  switch (kind) {
    case "boolean":
      return typeof raw === "boolean" ? raw : false;
    case "number":
      return typeof raw === "number" ? raw : 0;
    case "text":
      return typeof raw === "string" ? raw : "";
    case "stringList":
      return Array.isArray(raw) ? raw.filter((v) => typeof v === "string") : [];
    case "json":
      return raw == null ? "" : JSON.stringify(raw, null, 2);
  }
}

export function toFormValues(site: Site): SiteFormValues {
  const values: SiteFormValues = {};
  for (const field of SITE_FIELD_DESCRIPTORS) {
    values[field.key] = normalize(field.kind, site[field.key]);
  }
  return values;
}

export function emptyFormValues(): SiteFormValues {
  const values: SiteFormValues = {};
  for (const field of SITE_FIELD_DESCRIPTORS) {
    values[field.key] = emptyValueFor(field.kind);
  }
  return values;
}

function valuesEqual(a: FormValue, b: FormValue): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
  return a === b;
}

export class JsonFieldError extends Error {
  constructor(public readonly label: string) {
    super(`Invalid JSON in "${label}" — expected a JSON object or empty`);
    this.name = "JsonFieldError";
  }
}

type JsonObject = Record<string, unknown> | null;

// The connect-api jsonb fields are all proto Structs / typed messages, i.e.
// JSON objects (or null to clear). A scalar/array would be rejected server-side,
// so reject it here with a named error instead of shipping a guaranteed 4xx.
function parseJsonObjectField(text: string, label: string): JsonObject {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    throw new JsonFieldError(label);
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new JsonFieldError(label);
  }
  return parsed as Record<string, unknown>;
}

// Order-independent canonical form so re-serialising or reordering keys without
// a semantic change does not register as an edit (avoids a spurious PATCH).
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries
    .map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`)
    .join(",")}}`;
}

export function buildSitePatch(
  original: Site,
  current: SiteFormValues,
): { site: SitePatch; updateMask: string[] } {
  const originalValues = toFormValues(original);
  const site: SitePatch = {};
  const updateMask: string[] = [];

  for (const field of SITE_FIELD_DESCRIPTORS) {
    if (field.readOnly) continue;
    const next = current[field.key];
    if (next === undefined) continue;

    if (field.kind === "json") {
      const originalText =
        typeof originalValues[field.key] === "string"
          ? (originalValues[field.key] as string)
          : "";
      const nextText = typeof next === "string" ? next : "";
      const originalParsed = parseJsonObjectField(originalText, field.label);
      const nextParsed = parseJsonObjectField(nextText, field.label);
      if (stableStringify(originalParsed) !== stableStringify(nextParsed)) {
        site[field.key] = nextParsed;
        updateMask.push(field.key);
      }
      continue;
    }

    if (!valuesEqual(originalValues[field.key], next)) {
      site[field.key] = next;
      updateMask.push(field.key);
    }
  }

  return { site, updateMask };
}

export function fieldError(
  descriptor: SiteFieldDescriptor,
  value: FormValue,
): string | undefined {
  if (descriptor.kind === "json") {
    const text = typeof value === "string" ? value : "";
    let parsed: JsonObject;
    try {
      parsed = parseJsonObjectField(text, descriptor.label);
    } catch (err) {
      if (err instanceof JsonFieldError) return err.message;
      throw err;
    }
    if (descriptor.schema) {
      const result = descriptor.schema.safeParse(parsed);
      if (!result.success) {
        return result.error.issues[0]?.message ?? "Invalid value";
      }
    }
    return undefined;
  }

  if (descriptor.schema) {
    const result = descriptor.schema.safeParse(value);
    if (!result.success) {
      return result.error.issues[0]?.message ?? "Invalid value";
    }
  }
  return undefined;
}

function siteFieldChanged(
  field: SiteFieldDescriptor,
  originalValues: SiteFormValues,
  next: FormValue,
): boolean {
  if (field.kind === "json") {
    const originalText =
      typeof originalValues[field.key] === "string"
        ? (originalValues[field.key] as string)
        : "";
    const nextText = typeof next === "string" ? next : "";
    const originalParsed = parseJsonObjectField(originalText, field.label);
    let nextParsed: JsonObject;
    try {
      nextParsed = parseJsonObjectField(nextText, field.label);
    } catch {
      // Invalid JSON counts as a change; the error gate keeps Save disabled.
      return true;
    }
    return stableStringify(originalParsed) !== stableStringify(nextParsed);
  }
  return !valuesEqual(originalValues[field.key], next);
}

export function isSiteDirty(original: Site, current: SiteFormValues): boolean {
  const originalValues = toFormValues(original);
  return SITE_FIELD_DESCRIPTORS.some((field) => {
    if (field.readOnly) return false;
    const next = current[field.key];
    if (next === undefined) return false;
    return siteFieldChanged(field, originalValues, next);
  });
}

export type AnalogSequenceByTileId = Record<string, boolean>;

export function getAnalogSequenceChanges(
  original: AnalogSequenceByTileId,
  current: AnalogSequenceByTileId,
): Array<{ tileId: string; analog_sequence: boolean }> {
  return Object.entries(current)
    .filter(([tileId, value]) => original[tileId] !== value)
    .map(([tileId, analog_sequence]) => ({ tileId, analog_sequence }));
}

export function isAnalogSequenceDirty(
  original: AnalogSequenceByTileId,
  current: AnalogSequenceByTileId,
): boolean {
  return getAnalogSequenceChanges(original, current).length > 0;
}

// Only changed fields go on the wire, so a pre-existing invalid value the user
// didn't touch surfaces inline but must not block saving unrelated edits.
export function siteHasErrors(
  original: Site,
  current: SiteFormValues,
): boolean {
  const originalValues = toFormValues(original);
  return SITE_FIELD_DESCRIPTORS.some((field) => {
    const next = current[field.key];
    if (next === undefined) return false;
    if (!siteFieldChanged(field, originalValues, next)) return false;
    return fieldError(field, next) !== undefined;
  });
}

export type SitePickerOption = Pick<
  SiteListing,
  "rj_id" | "brand_name" | "url"
>;

export function siteListingDisplayName(listing: SitePickerOption): string {
  return listing.brand_name || listing.url || listing.rj_id;
}

export type SiteSortField = "url" | "brand" | "account";
export type SortDirection = "asc" | "desc";

export interface SiteListFilters {
  q?: string;
  account?: number;
  isConnect?: boolean;
  isAnalog?: boolean;
}

export function filterSiteListings(
  sites: SiteListing[],
  filters: SiteListFilters,
  accountName: (id: number) => string,
): SiteListing[] {
  const q = (filters.q ?? "").trim().toLowerCase();
  return sites.filter((s) => {
    const matchesQ =
      q === "" ||
      s.url.toLowerCase().includes(q) ||
      s.brand_name.toLowerCase().includes(q) ||
      s.rj_id.toLowerCase().includes(q) ||
      accountName(s.account_id).toLowerCase().includes(q);
    const matchesAccount =
      filters.account === undefined || s.account_id === filters.account;
    const matchesConnect =
      filters.isConnect === undefined || s.is_connect === filters.isConnect;
    const matchesAnalog =
      filters.isAnalog === undefined ||
      s.is_analog_sequence_enabled === filters.isAnalog;
    return matchesQ && matchesAccount && matchesConnect && matchesAnalog;
  });
}

export function sortSiteListings(
  sites: SiteListing[],
  field: SiteSortField | undefined,
  direction: SortDirection | undefined,
  accountName: (id: number) => string,
): SiteListing[] {
  if (!field) return sites;
  const dir = direction === "desc" ? -1 : 1;
  const value = (s: SiteListing) => {
    if (field === "url") return s.url;
    if (field === "brand") return s.brand_name;
    return accountName(s.account_id);
  };
  return [...sites].sort(
    (a, b) =>
      value(a).localeCompare(value(b), undefined, { sensitivity: "base" }) *
      dir,
  );
}
